import type { FeedingPen } from './penTypes';
import type { FeedingSummaryCoverage } from './summaryTypes';
import type { FeedingDataSources } from './types';

/** A summary value that averages or totals over the selected pens. */
export type KpiMetric = Exclude<keyof FeedingSummaryCoverage, 'selectedPens'>;

/** Any per-pen value: the summary's, plus the ones only the pen table shows. */
export type PenMetric = KpiMetric | 'fishCount' | 'feedVsPlan' | 'sfrPms' | 'sfrBioscope';

/**
 * Why a selected pen is not part of a summary value, most specific first:
 * - `feedingServiceUnavailable`: service-feeding-rest did not answer for the pen;
 * - `noBioscope`: the pen had no Bioscope in the period;
 * - `noBioscopeData`: it had one, which produced no estimate in the period;
 * - `collecting`: its Bioscope is still inside the startup window;
 * - `noFeedingCamera`: pellet fallthrough needs Bioscope 3.0 hardware;
 * - `notConfigured`: a planning-system signal the value needs is not mapped for the pen;
 * - `noValue`: everything is in place and no value arrived.
 */
export type KpiExclusionReason =
    | 'feedingServiceUnavailable'
    | 'noBioscope'
    | 'noBioscopeData'
    | 'collecting'
    | 'noFeedingCamera'
    | 'notConfigured'
    | 'noValue';

interface MetricRule {
    /** Counts ready pens only (IPS 2.1.4). */
    bioscope: boolean;
    /** Read from service-feeding-rest, so a failed call for the pen blanks it. */
    service: boolean;
    /** The planning-system signals the value is computed from. */
    signals: (keyof FeedingDataSources)[];
}

// Weight BS is the only Bioscope value that survives a failed service call: without the
// Kalman trend it falls back to the day's own estimate.
const RULES: Record<PenMetric, MetricRule> = {
    weightBioscope: { bioscope: true, service: false, signals: [] },
    weightPmsPlan: { bioscope: false, service: true, signals: ['weightPmsPlan'] },
    weightPmsActual: { bioscope: false, service: true, signals: ['weightPmsActual'] },
    fcrPms: { bioscope: false, service: true, signals: ['fcrPms'] },
    fcrBioscope: { bioscope: true, service: true, signals: [] },
    rgiFeed: { bioscope: false, service: true, signals: ['weightPmsActual', 'weightPmsPlan'] },
    rgiBioscope: { bioscope: true, service: true, signals: ['weightPmsPlan'] },
    feedActual: { bioscope: false, service: true, signals: ['feedActual'] },
    feedPlan: { bioscope: false, service: true, signals: ['feedPlan'] },
    fallthrough: { bioscope: false, service: false, signals: [] },
    fishCount: { bioscope: false, service: true, signals: ['fishCount'] },
    feedVsPlan: { bioscope: false, service: true, signals: ['feedActual', 'feedPlan'] },
    sfrPms: {
        bioscope: false,
        service: true,
        signals: ['feedActual', 'weightPmsActual', 'fishCount'],
    },
    sfrBioscope: { bioscope: true, service: true, signals: ['feedActual', 'fishCount'] },
};

function bioscopeReason(pen: FeedingPen): KpiExclusionReason | null {
    if (pen.bioscopeDataQuality === 'ready') return null;
    if (pen.bioscopeDataQuality === 'collecting') return 'collecting';
    return pen.bioscopeInPeriod ? 'noBioscopeData' : 'noBioscope';
}

/**
 * The reason a pen has no part in `metric`. Meant for pens a value leaves out; for a pen
 * that contributes it still answers, and then says what would blank it.
 */
export function kpiExclusionReason(pen: FeedingPen, metric: PenMetric): KpiExclusionReason {
    const rule = RULES[metric];
    // The failed call is the cause of everything below it, so it is named first: the pen's
    // sources otherwise read as silent, which would blame the planning system.
    if (rule.service && pen.feedingServiceUnavailable) return 'feedingServiceUnavailable';
    if (rule.bioscope) {
        const reason = bioscopeReason(pen);
        if (reason) return reason;
    }
    if (metric === 'fallthrough' && !pen.fallthroughSupported) return 'noFeedingCamera';
    if (rule.signals.some((signal) => pen.dataSources[signal] === 'notConfigured')) {
        return 'notConfigured';
    }
    return 'noValue';
}

export interface LeftOutPen {
    pen: FeedingPen;
    reason: KpiExclusionReason;
}

/** The selected pens `metric` leaves out, in selection order, each with its reason. */
export function leftOutPens(
    pens: FeedingPen[],
    coverage: FeedingSummaryCoverage,
    metric: KpiMetric
): LeftOutPen[] {
    const included = new Set(coverage[metric]);
    return pens
        .filter((pen) => !included.has(pen.cageId))
        .map((pen) => ({ pen, reason: kpiExclusionReason(pen, metric) }));
}
